import { StoreEnhancer } from "redux"
import { PayloadAction, configureStore } from "@reduxjs/toolkit"

import { epicMiddleware, rootEpic } from "./epics"

import addins from "./components/addins/reducer"
import auth from "./components/auth/reducer"
import bookmarks from "./components/bookmarks/reducer"
import cardView from "./components/cardview/reducer"
import conceptsPage from "./components/conceptspage/reducer"
import confirmationDialog from "./components/confirmationdialog/reducer"
import contextualHelp from "./components/contextualhelp/reducer"
import contracts from "./components/contracts/reducer"
import dataAPIBookmarkSlice from "./components/dataapi-bookmark/slice"
import deployment from "./components/deployment/reducer"
import fieldsSlice from "./components/fields/slice"
import footerSlice from "./components/footer/slice"
import indicators from "./components/indicators/reducer"
import inlineAttachments from "./components/inlineattachments/reducer"
import itemBoxContextMenuSlice from "./components/itemboxcontextmenu/slice"
import itemDetailSlice from "./components/itemdetail/slice"
import navigation from "./components/applicationheader/components/navigation/reducer"
import notifications from "./components/notifications/reducer"
import pendingNotifications from "./components/applicationheader/components/pendingnotifications/reducer"
import printViewSlice from "./components/printView/slice"
import resourceFooter from "./components/resourcefooter/reducer"
import resourceHeader from "./components/resourceheader/reducer"
import resourcesSlice from "./components/resources/slice"
import richTextSlice, {
    IRichTextState,
} from "./components/fields/components/richText/slice"
import scriptingSlice from "./components/scripting/slice"
import search from "./components/search/reducer"
import shortcuts from "./components/shortcuts/reducer"
import smartLocation from "./components/applicationheader/components/smartlocation/reducer"
import stickyFooterSlice from "./components/stickyFooter/slice"
import thumbTableView from "./components/thumbtableview/slice"
import tutorial from "./components/tutorial/reducer"
import userFormattingSettingsSlice from "./components/common/userFormattingSettings/slice"
import userInfo from "./components/applicationheader/components/usermenu/reducer"
import welcomePage from "./components/welcomepage/reducer"
import workspaceCoverPage from "./components/workspacecoverpage/reducer"
import workspaceListPage from "./components/workspacelistpage/reducer"

import { IAddins } from "./components/addins"
import { IAuth } from "./components/auth"
import { IBookmarks } from "./components/bookmarks"
import { ICardView } from "./components/cardview"
import { IConceptsPage } from "./components/conceptspage"
import { IConfirmationDialog } from "./components/confirmationdialog"
import { IContextualHelp } from "./components/contextualhelp"
import { IContracts } from "./components/contracts"
import { ICoverPage } from "./components/workspacecoverpage"
import { IDataAPIBookmarkButton } from "./components/dataapi-bookmark"
import { IDeployment } from "./components/deployment"
import { IFooter } from "./components/footer"
import { IHeader } from "./components/resourceheader"
import { IIndicators } from "./components/indicators"
import { IInlineAttachments } from "./components/inlineattachments"
import { IItem } from "./models"
import { IItemBoxContextMenu } from "./components/itemboxcontextmenu"
import { IItemDetailState } from "./components/itemdetail"
import { INavigation } from "./components/applicationheader/components/navigation"
import { INotifications } from "./components/notifications"
import { IPendingNotifications } from "./components/applicationheader/components/pendingnotifications"
import { IPrintView } from "./components/printView"
import { IResourceCatalog } from "./components/resources"
import { IResourceFooter } from "./components/resourcefooter"
import { ISearch } from "./components/search"
import { IShortcuts } from "./components/shortcuts"
import { ISmartLocation } from "./components/applicationheader/components/smartlocation"
import { IStickyFooter } from "./components/stickyFooter"
import { IThumbTable } from "./components/thumbtableview"
import { ITutorial } from "./components/tutorial"
import { IWelcomePage } from "./components/welcomepage"
import { IWorkspaceListPage } from "./components/workspacelistpage"
import {
    IUserFormattingSettings,
    IUserInfo,
} from "./components/applicationheader/components/usermenu"

export interface GlobalState {
    addins: IAddins
    auth: IAuth
    bookmarks: IBookmarks
    cardView: ICardView
    conceptsPage: IConceptsPage
    confirmationDialog: IConfirmationDialog
    contextualHelp: IContextualHelp
    contracts: IContracts
    dataAPIBookmark: IDataAPIBookmarkButton
    deployment: IDeployment
    fields: IItem
    footer: IFooter
    indicators: IIndicators
    inlineAttachments: IInlineAttachments
    itemBoxContextMenu: IItemBoxContextMenu
    itemDetail: IItemDetailState
    navigation: INavigation
    notifications: INotifications
    pendingNotifications: IPendingNotifications
    printView: IPrintView
    resourceFooter: IResourceFooter
    resourceHeader: IHeader
    resources: IResourceCatalog
    richText: IRichTextState
    search: ISearch
    shortcuts: IShortcuts
    smartLocation: ISmartLocation
    stickyFooter: IStickyFooter
    thumbTableView: IThumbTable
    tutorial: ITutorial
    userFormattingSettings: IUserFormattingSettings
    userInfo: IUserInfo
    welcomePage: IWelcomePage
    workspaceCoverPage: ICoverPage
    workspaceListPage: IWorkspaceListPage
}

const reducers = {
    addins,
    auth,
    bookmarks,
    cardView,
    conceptsPage,
    confirmationDialog,
    contextualHelp,
    contracts,
    dataAPIBookmark: dataAPIBookmarkSlice,
    deployment,
    fields: fieldsSlice,
    footer: footerSlice,
    indicators,
    inlineAttachments,
    itemBoxContextMenu: itemBoxContextMenuSlice,
    itemDetail: itemDetailSlice,
    navigation,
    notifications,
    pendingNotifications,
    printView: printViewSlice,
    resourceFooter,
    resourceHeader,
    resources: resourcesSlice,
    richText: richTextSlice,
    scripting: scriptingSlice,
    search,
    shortcuts,
    smartLocation,
    stickyFooter: stickyFooterSlice,
    thumbTableView,
    tutorial,
    userFormattingSettings: userFormattingSettingsSlice,
    userInfo,
    welcomePage,
    workspaceCoverPage,
    workspaceListPage,
}

// using the npm package for react dev tools: https://github.com/zalmoxisus/redux-devtools-extension#13-use-redux-devtools-extension-package-from-npm
// using the following recipe: https://github.com/zalmoxisus/redux-devtools-extension/blob/master/docs/Recipes.md#using-in-a-typescript-project
type WindowWithDevTools = Window & {
    __REDUX_DEVTOOLS_EXTENSION__: () => StoreEnhancer<unknown, unknown>
}

const isReduxDevtoolsExtenstionExist = (
    arg: Window | WindowWithDevTools,
): arg is WindowWithDevTools => {
    return "__REDUX_DEVTOOLS_EXTENSION__" in arg
}

export const store = configureStore({
    reducer: reducers,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware().concat(epicMiddleware),
    devTools: __DEBUG__ && !!isReduxDevtoolsExtenstionExist,
})

epicMiddleware.run(rootEpic)

// See https://react-redux.js.org/tutorials/typescript-quick-start#define-typed-hooks
// Infer the `AppDispatch` types from the store itself
// the RootState is the GlobalState in our case
export type AppDispatch = typeof store.dispatch

export type AppStore = typeof store

export type PayloadActionType<T> = PayloadAction<T> & {
    meta: {
        arg: unknown
        requestId: string
        requestStatus: "fulfilled" | "rejected" | "pending"
    }
}
